import { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useFragment, graphql } from 'react-relay';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { getCurrency } from 'dibs-intl/exports/units';
import { useAbTestV2BuyerUser } from 'dibs-buyer-layout/exports/clientABTestV2';

import styles from './styles/CheckoutTotals.scss';
import { connect } from 'react-redux';
import { CheckoutServerVarsContext } from '../components/shared/CheckoutServerVarsContext';
import { getShippingAddressById } from '../helpers/checkoutHelpers';
import { FormattedMessage } from 'dibs-react-intl';
import { isOnReviewPane } from '../reducers/checkoutPaneReducer';
import { useCheckoutSelector } from '../reducers/root';
import { vatMessages } from 'dibs-vat-utils/exports/vatMessages';
import { isDapDdpCheckoutVariant } from '../shared/helpers/dapDdpCheckoutVariantHelpers';

// components
import CheckoutSidebarPricingRow from './CheckoutSidebarPricingRow';
import CheckoutFormatAmount from '../components/shared/CheckoutFormatAmount';
import CheckoutSelectQuantity from '../components/shared/CheckoutSelectQuantity';
import CheckoutTextWithTooltip from '../components/shared/CheckoutTextWithTooltip';

import CheckoutShippingPromoTooltip from '../components/shared/CheckoutShippingPromoTooltip';
import LocalSpinner from '../shared/components/LocalSpinner';
import CheckoutSidebarChargesDisclaimer from './CheckoutSidebarChargesDisclaimer';
import { CheckoutDutiesMessage } from '../shared/helpers/CheckoutDutiesMessage';

const messages = defineMessages({
    total: {
        id: 'checkout.CheckoutTotals.total',
        defaultMessage: 'Total',
    },
    totalCharge: {
        id: 'checkout.CheckoutTotals.totalCharge',
        defaultMessage: "You'll be charged",
    },
    cardWontBeCharged: {
        id: 'checkout.CheckoutTotals.cardWontBeCharged',
        defaultMessage:
            "A payment method is required to complete any purchases on 1stDibs, but you won't be charged.",
    },
});

const CheckoutTotals = props => {
    const {
        item: itemRef,
        transaction: transactionRef,
        viewer: viewerRef,
        shippingAddressId,
        wrapperClass,
        canSelectChargeCurrency,
        buyer: buyerRef,
        buyerChargeCurrency,
        hasQuantity,
        isComputing,
        isTaxExempt,
        customsMessagingQueryReference,
    } = props;

    const paneState = useCheckoutSelector(state => state.pane);

    const viewer = useFragment(
        graphql`
            fragment CheckoutTotals_viewer on Viewer {
                ...CheckoutSidebarChargesDisclaimer_viewer
                ...CheckoutDutiesMessage_viewer
                newDutiesMsgAndTaxEnabled: featureFlag(feature: "newDutiesMessageAndTax")
            }
        `,
        viewerRef
    );
    const item = useFragment(
        graphql`
            fragment CheckoutTotals_item on Item {
                vertical
                canMultipleQuantityBePurchased
                serviceId
                isJewelry
            }
        `,
        itemRef
    );
    const buyer = useFragment(
        graphql`
            fragment CheckoutTotals_buyer on User {
                serviceId
                isVerifiedTrade: hasPermission(name: "B_NET_PRICE")
                ...clientAbTestV2_useAbTestV2BuyerUser_user
                ...checkoutHelpers_buyer
            }
        `,
        buyerRef
    );
    const transaction = useFragment(
        graphql`
            fragment CheckoutTotals_transaction on Transaction {
                ...CheckoutSelectQuantity_transaction
                ...CheckoutSidebarChargesDisclaimer_transaction
                ...CheckoutDutiesMessage_transaction
                ...CheckoutTotalsDutiesMessage_transaction
                isShippingKnownAtCheckout
                isNegotiable
                buyerInvoice {
                    totalSalesTax {
                        amount
                    }
                }
                buyerPriceTables(displayCurrency: $displayCurrency) {
                    tables {
                        type
                        rows {
                            label
                            value
                            rowType
                            formatStyle
                            hasTopBorder
                            subLabel
                        }
                    }
                }
                summary {
                    invoiceSummary {
                        buyerCurrentInvoice {
                            totalAmount {
                                amount
                                ...CheckoutFormatAmount_financialAmount
                            }
                        }
                    }
                }
                canBeTaxExempt
                isVatOrder
                isVatReverseChargeApplied
                priceBookName
                currentOrSubmittedOrNewShipmentQuote {
                    serviceMethod {
                        serviceType
                    }
                    author {
                        role
                    }
                    isShipmentSubsidizedByDibs
                    isShipmentSubsidized
                }
                shipment {
                    isShippingWithinUS
                }
            }
        `,
        transactionRef
    );

    const { isMobile } = useContext(CheckoutServerVarsContext);
    const { vertical, isJewelry } = item;
    const { isVerifiedTrade } = buyer || {};
    const {
        isShippingKnownAtCheckout,
        canBeTaxExempt,
        isNegotiable,
        isVatOrder,
        isVatReverseChargeApplied,
        priceBookName,
        shipment,
        currentOrSubmittedOrNewShipmentQuote,
    } = transaction;

    const intl = useIntl();
    const defaultCurrency = getCurrency(intl.locale);
    const currency = buyerChargeCurrency || defaultCurrency;
    const financialAmount = transaction?.summary?.invoiceSummary?.buyerCurrentInvoice?.totalAmount;
    const isShippingWithinUS = !!shipment?.isShippingWithinUS;
    const { serviceMethod, author, isShipmentSubsidizedByDibs, isShipmentSubsidized } =
        currentOrSubmittedOrNewShipmentQuote || {};
    const isFreeQuoteToBuyer = isShipmentSubsidizedByDibs || isShipmentSubsidized;

    useAbTestV2BuyerUser({
        testName: 'RTQuote-Prequote-US-Q3-2023',
        user: buyer,
        shouldFireTracking:
            isShippingWithinUS &&
            serviceMethod?.serviceType === 'PARCEL' &&
            author?.role !== 'SELLER' &&
            !isFreeQuoteToBuyer &&
            !isJewelry,
    });

    const priceTable = (transaction?.buyerPriceTables?.tables || []).find(
        ({ type }) => type === 'MAIN'
    );
    const tableRows = priceTable?.rows ? [...priceTable.rows] : [];
    const listPrice = tableRows.find(row => row?.rowType === 'LIST_PRICE');
    const hasNonListPrices = tableRows.some(row => row?.rowType === 'PRICE');

    const hasMultipleQuantity = item?.canMultipleQuantityBePurchased;

    const newDutiesMsgAndTaxEnabled = viewer?.newDutiesMsgAndTaxEnabled;

    if (canSelectChargeCurrency) {
        const rowTotalCharge = {
            rowType: 'TOTAL_CHARGE',
            label: intl.formatMessage(messages.totalCharge),
            value: hasQuantity ? (
                <CheckoutFormatAmount financialAmount={financialAmount} currency={currency} />
            ) : (
                '--'
            ),
        };
        tableRows.push(rowTotalCharge);
    }

    if (!financialAmount || financialAmount.amount === 0) {
        const rowCardWontBeCharged = {
            rowType: 'CARD_WONT_BE_CHARGED',
            label: intl.formatMessage(messages.cardWontBeCharged),
        };
        tableRows.push(rowCardWontBeCharged);
    }

    return (
        <div
            className={classNames(styles.wrapper, wrapperClass, {
                [styles.mobileWrapper]: isMobile,
                [styles.wrapperTopBorder]: !isNegotiable,
            })}
            data-tn="checkout-totals-table"
        >
            {tableRows.map((row, index) => {
                const { rowType, label, subLabel, value } = row;

                switch (rowType) {
                    case 'LIST_PRICE':
                        return hasNonListPrices ? null : (
                            <CheckoutSidebarPricingRow {...row} key={`${rowType}-${index}`} />
                        );
                    case 'PRICE': {
                        return (
                            <CheckoutSidebarPricingRow
                                {...row}
                                hasTopBorder={false}
                                discountFrom={listPrice?.value}
                                key={`${rowType}-${index}`}
                                label={
                                    subLabel ? (
                                        <span>
                                            {label}
                                            <span className={styles.subLabel}>{subLabel}</span>
                                        </span>
                                    ) : (
                                        label
                                    )
                                }
                            />
                        );
                    }
                    case 'QUANTITY':
                        return (
                            <CheckoutSidebarPricingRow
                                {...row}
                                value={
                                    hasMultipleQuantity ? (
                                        <CheckoutSelectQuantity transaction={transaction} />
                                    ) : (
                                        value
                                    )
                                }
                                key={`${rowType}-${index}`}
                            />
                        );
                    case 'SHIPPING':
                        return (
                            <CheckoutSidebarPricingRow
                                {...row}
                                value={
                                    shippingAddressId && !isShippingKnownAtCheckout && !isMobile ? (
                                        <CheckoutTextWithTooltip
                                            text={value}
                                            tooltip={
                                                <FormattedMessage
                                                    id="checkout.CheckoutShippingTooltipNew.shippingTooltip"
                                                    defaultMessage="The {vertical, select, art {gallery} other {seller}} will send you a quote for the shipping cost within 48 hours. If the {vertical, select, art {gallery} other {seller}} provides complimentary shipping, your order will be confirmed instantly. Otherwise, we will ask you to approve the shipping cost before confirming your purchase."
                                                    values={{ vertical }}
                                                />
                                            }
                                            addQuestionMark
                                            questionMarkIconClass={styles.questionMarkIcon}
                                        />
                                    ) : (
                                        value
                                    )
                                }
                                key={`${rowType}-${index}`}
                            />
                        );
                    case 'PROMO':
                        return (
                            <CheckoutSidebarPricingRow
                                {...row}
                                value={
                                    shippingAddressId && !isShippingKnownAtCheckout && value ? (
                                        <CheckoutShippingPromoTooltip text={row.value} />
                                    ) : (
                                        value
                                    )
                                }
                                key={`${rowType}-${index}`}
                            />
                        );
                    case 'SALES_TAX': {
                        const shippingAddress = getShippingAddressById({
                            shippingAddressId,
                            buyer,
                        });
                        const showAsterisk = isMobile
                            ? canBeTaxExempt && !isTaxExempt
                            : isVerifiedTrade && !!shippingAddress?.state && !isTaxExempt;
                        const _value = showAsterisk ? `${value}*` : value;
                        return (
                            <CheckoutSidebarPricingRow
                                {...row}
                                value={
                                    isComputing ? (
                                        <span className={styles.pricingWrapper}>
                                            <LocalSpinner
                                                inline
                                                wrapperClass={styles.spinnerWrapper}
                                            />
                                            {_value}
                                        </span>
                                    ) : (
                                        _value
                                    )
                                }
                                key={`${rowType}-${index}`}
                            />
                        );
                    }
                    case 'BUYER_DUTIES_TAX': {
                        return newDutiesMsgAndTaxEnabled && isDapDdpCheckoutVariant ? (
                            <CheckoutSidebarPricingRow
                                {...row}
                                value={
                                    isComputing ? (
                                        <span>
                                            <LocalSpinner inline />
                                            {value}
                                        </span>
                                    ) : (
                                        value
                                    )
                                }
                                label={label}
                                key={`${rowType}-${index}`}
                            />
                        ) : null;
                    }
                    case 'TOTAL':
                    case 'TOTAL_CHARGE':
                        return (
                            <div key={`${rowType}-${index}`}>
                                <CheckoutSidebarPricingRow
                                    {...row}
                                    isMainPrice
                                    value={
                                        isComputing ? (
                                            <span className={styles.pricingWrapper}>
                                                <LocalSpinner
                                                    inline
                                                    wrapperClass={styles.spinnerWrapper}
                                                />
                                                {value}
                                            </span>
                                        ) : (
                                            value
                                        )
                                    }
                                />
                                {isVatOrder && !isVatReverseChargeApplied && (
                                    <div data-tn="vat-label" className={styles.vatLabel}>
                                        {vatMessages.included({ priceBookName })}
                                    </div>
                                )}
                                {isVatReverseChargeApplied && (
                                    <div data-tn="vat-label" className={styles.vatLabel}>
                                        {vatMessages.reverseCharge({ priceBookName })}
                                    </div>
                                )}
                            </div>
                        );
                    case 'PRICE_DISCOUNT':
                    case 'NEGOTIATED_DISCOUNT':
                        return null;
                    default:
                        return (
                            <CheckoutSidebarPricingRow
                                hasTopBorder={false}
                                {...row}
                                key={`${rowType}-${index}`}
                            />
                        );
                }
            })}
            <CheckoutDutiesMessage
                viewer={viewer}
                isComputing={isComputing}
                transaction={transaction}
                currency={currency}
                queryReference={customsMessagingQueryReference}
            />
            {isOnReviewPane(paneState) && (
                <CheckoutSidebarChargesDisclaimer transaction={transaction} viewer={viewer} />
            )}
        </div>
    );
};

CheckoutTotals.propTypes = {
    item: PropTypes.object.isRequired,
    buyer: PropTypes.object,
    viewer: PropTypes.object,
    transaction: PropTypes.object.isRequired,
    wrapperClass: PropTypes.string,
    shippingAddressId: PropTypes.string,
    buyerChargeCurrency: PropTypes.string,
    hasQuantity: PropTypes.bool,
    hasQuantityError: PropTypes.bool,
    canSelectChargeCurrency: PropTypes.bool,
    isComputing: PropTypes.bool,
    isTaxExempt: PropTypes.bool,
    customsMessagingQueryReference: PropTypes.object,
};

CheckoutTotals.defaultTypes = {
    wrapperClass: '',
};

export default connect(({ ui, transaction }) => ({
    hasQuantityError: ui.hasQuantityError,
    canSelectChargeCurrency: ui.canSelectChargeCurrency,
    shippingAddressId: transaction.shippingAddressId,
    buyerChargeCurrency: transaction.buyerChargeCurrency,
    hasQuantity: !!transaction.quantity,
    isTaxExempt: transaction.isTaxExempt,
}))(CheckoutTotals);
